import {
  BulkDownloadPayslips,
  BulkNotifyPayslips,
  BulkGenerateFromPayroll,
  GeneratePayslipFromPayroll,
} from '@/api/Payroll/userPayslip';
import { type InternalEmployee, type DisplayEmployee, type DepartmentFromAPI, getMonthName, getDepartmentId } from './payslipDataTransformers';

type ShowToast = (message: string, type: 'success' | 'error' | 'info') => void;
type TFunc = (key: string, options?: any) => string;

interface BulkOperationInput {
  userIds?: string[];
  department?: string;
  payrollCycle?: string;
  month: string;
  year: string;
  generatePdf: boolean;
  sendEmail: boolean;
  includeVariablePay?: boolean;
  includeAdjustmentAdditions?: boolean;
  includeAdjustmentDeductions?: boolean;
  includeDeductions?: boolean;
  includeTaxCalculations?: boolean;
}

export interface BulkOperationDeps {
  showToast: ShowToast;
  t: TFunc;
  internalEmployees: InternalEmployee[];
  filters: { month: string; year: string };
  findDepartmentId: (dept: any) => number | undefined;
  loadEmployees: () => Promise<void>;
}

export async function executeBulkConfirmation(
  bulkOperation: 'download' | 'notify' | 'generate',
  selectedPayslips: number[],
  deps: BulkOperationDeps
): Promise<void> {
  const { showToast, t, internalEmployees, filters, loadEmployees } = deps;

  switch (bulkOperation) {
    case 'download':
      await BulkDownloadPayslips(selectedPayslips);
      showToast(t('Download initiated for') + ` ${selectedPayslips.length} ` + t('payslip(s)'), 'success');
      break;
    case 'notify': {
      const notifyResult = await BulkNotifyPayslips({ payslipIds: selectedPayslips });
      if (notifyResult.status === 200) {
        showToast(t('Notifications sent for') + ` ${selectedPayslips.length} ` + t('payslip(s)'), 'success');
      } else {
        showToast(t('Failed to send some notifications'), 'error');
      }
      break;
    }
    case 'generate': {
      const yr = parseInt(filters.year || new Date().getFullYear().toString());
      const monthNum = parseInt(filters.month || (new Date().getMonth() + 1).toString().padStart(2, '0'));
      const selectedUserIds = selectedPayslips
        .map(id => internalEmployees.find(emp => emp.id === id)?.userId)
        .filter((uid): uid is string => !!uid);

      const generateResult = await BulkGenerateFromPayroll({
        userIds: selectedUserIds,
        month: monthNum,
        year: yr,
        includeVariablePay: true,
        includeAdjustmentAdditions: true,
        includeAdjustmentDeductions: true,
        includeDeductions: true,
        includeTaxCalculations: false,
        generatePdf: true,
        sendEmailNotification: false,
      });
      if (generateResult.status === 200) {
        showToast(t('Generated') + ` ${selectedPayslips.length} ` + t('payslip(s)'), 'success');
        setTimeout(() => loadEmployees(), 1000);
      } else {
        showToast(generateResult.errors?.[0]?.description || t('Failed to generate payslips'), 'error');
      }
      break;
    }
  }
}

export async function executePayslipGeneration(
  employee: DisplayEmployee,
  deps: BulkOperationDeps
): Promise<void> {
  const { showToast, t, internalEmployees, filters, findDepartmentId, loadEmployees } = deps;
  const internalEmployee = internalEmployees.find(emp => emp.id === employee.id);
  const departmentId = internalEmployee ? findDepartmentId(internalEmployee.department) : undefined;
  const mo = filters.month || (new Date().getMonth() + 1).toString().padStart(2, '0');
  const yr = parseInt(filters.year || new Date().getFullYear().toString());
  const payrollDate = new Date(yr, parseInt(mo) - 1, 1);

  const result = await GeneratePayslipFromPayroll({
    userId: employee.userId,
    departmentId,
    payrollDate: payrollDate.toISOString() as any,
    basicSalary: employee.basicSalary || 0,
    variablePay: employee.variablePay || 0,
    deductions: employee.deductions || 0,
    includeVariablePay: true,
    includeAdjustmentAdditions: true,
    includeAdjustmentDeductions: true,
    includeDeductions: true,
    includeTaxCalculations: false,
    generatePdf: true,
    sendEmailNotification: false,
  });
  if (result.status === 200) {
    showToast(t('Payslip generated successfully!'), 'success');
    setTimeout(() => loadEmployees(), 1000);
  } else {
    showToast(result.errors?.[0]?.description || t('Failed to generate payslip'), 'error');
  }
}

export async function executeBulkGenerationByDepartment(
  input: BulkOperationInput,
  departmentsFromAPI: DepartmentFromAPI[],
  deps: Pick<BulkOperationDeps, 'showToast' | 't' | 'loadEmployees'>
): Promise<void> {
  const { showToast, t, loadEmployees } = deps;
  const targetMonth = input.month ? parseInt(input.month) : new Date().getMonth() + 1;
  const targetYear = input.year ? parseInt(input.year) : new Date().getFullYear();

  // Resolve department: accept numeric ID directly, or fall back to name-based lookup
  let departmentId: number | undefined;
  let departmentName: string = 'all departments';
  if (input.department) {
    const numericDept = typeof input.department === 'number' ? input.department : parseInt(input.department, 10);
    if (!isNaN(numericDept)) {
      departmentId = numericDept;
      departmentName = departmentsFromAPI.find(d => d.id === numericDept)?.name || `Department ${numericDept}`;
    } else {
      departmentId = departmentsFromAPI.find(d => d.name.toLowerCase() === input.department?.toString().toLowerCase())?.id;
      departmentName = input.department.toString();
    }
  }

  let targetUserIds: string[] | undefined;
  if (input.userIds?.length) targetUserIds = input.userIds;

  const monthYear = `${t('for')} ${getMonthName(targetMonth.toString())} ${targetYear}`;
  showToast(`${t('Generating payslips')} ${monthYear} ${t('for')} ${departmentName}...`, 'info');

  const result = await BulkGenerateFromPayroll({
    userIds: targetUserIds,
    department: departmentId,
    month: targetMonth,
    year: targetYear,
    includeVariablePay: input.includeVariablePay ?? true,
    includeAdjustmentAdditions: input.includeAdjustmentAdditions ?? true,
    includeAdjustmentDeductions: input.includeAdjustmentDeductions ?? true,
    includeDeductions: input.includeDeductions ?? true,
    includeTaxCalculations: input.includeTaxCalculations ?? false,
    generatePdf: input.generatePdf ?? true,
    sendEmailNotification: input.sendEmail ?? false,
  });

  if (result.status === 200) {
    showToast(`${t('Successfully generated payslips for')} ${departmentName} ${monthYear}.`, 'success');
    await loadEmployees();
    setTimeout(async () => await loadEmployees(), 3000);
  } else {
    if (result.errors?.length > 0) {
      let errorMessage = result.errors[0].description || t('Failed to generate payslips');
      if (errorMessage.toLowerCase().includes('no payroll data') || errorMessage.toLowerCase().includes('no salary')) {
        errorMessage = t('No payroll data found. Please ensure salary information is configured.');
      } else if (errorMessage.toLowerCase().includes('permission') || errorMessage.toLowerCase().includes('unauthorized')) {
        errorMessage = t("You don't have permission to generate payslips.");
      }
      showToast(errorMessage, 'error');
    } else {
      showToast(t('Failed to generate payslips. Please try again later.'), 'error');
    }
  }
}

export async function executeCreatePayslip(
  employee: DisplayEmployee,
  deps: BulkOperationDeps
): Promise<void> {
  const { showToast, t, internalEmployees, filters, loadEmployees } = deps;
  const internalEmployee = internalEmployees.find(emp => emp.id === employee.id);
  if (!internalEmployee) {
    showToast(t('Employee data not found'), 'error');
    return;
  }
  const departmentId = internalEmployee.departmentId ?? getDepartmentId(internalEmployee.department);
  if (!departmentId) {
    showToast(t('Department information is required'), 'error');
    return;
  }
  const mo = filters.month || (new Date().getMonth() + 1).toString().padStart(2, '0');
  const yr = parseInt(filters.year || new Date().getFullYear().toString());
  const payrollDate = new Date(yr, parseInt(mo) - 1, 1);

  const result = await GeneratePayslipFromPayroll({
    userId: employee.userId,
    departmentId,
    payrollDate: payrollDate.toISOString(),
    basicSalary: employee.basicSalary || 0,
    variablePay: employee.variablePay || 0,
    deductions: employee.deductions || 0,
    includeVariablePay: true,
    includeAdjustmentAdditions: true,
    includeAdjustmentDeductions: true,
    includeDeductions: true,
    includeTaxCalculations: false,
    generatePdf: false,
    sendEmailNotification: false,
    status: 'Draft',
  } as any);

  if (result.status === 200) {
    showToast(t('Payslip created successfully!'), 'success');
    setTimeout(() => loadEmployees(), 1000);
  } else {
    showToast(result.errors?.[0]?.description || t('Failed to create payslip'), 'error');
  }
}
